import { useState, useEffect, useCallback, useRef } from "react";
import { auth, db } from "../firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  updateEmail as firebaseUpdateEmail,
  updatePassword as firebaseUpdatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { STATUS_PENDING, STATUS_BLOCKED, AGENCY_ACTIVE } from "../constants";
import logger from "../utils/logger";
import type { User } from "../types";

// AuthError carries Firebase's error.code (e.g. "auth/pending-approval") so
// callers can branch on it; plain `Error` doesn't have a `code` field.
class AuthError extends Error {
  code: string;
  constructor(message: string, code: string) {
    super(message);
    this.code = code;
  }
}

// Best-effort call to the server auth-event audit log. Never throws —
// a broken audit sink must not prevent users from signing in or out.
async function recordAuthEvent(event: string, extra: Record<string, unknown> = {}) {
  try {
    const functions = getFunctions();
    const logAuthEvent = httpsCallable(functions, "logAuthEvent");
    await logAuthEvent({ event, ...extra });
  } catch (err) {
    logger.warn("Failed to record auth event:", event, (err as Error)?.message);
  }
}

function friendlyAuthError(error: unknown): string {
  const code = (error as { code?: string })?.code || "";
  switch (code) {
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Incorrect email or password. Please try again.";
    case "auth/user-not-found":
      return "No account found with that email.";
    case "auth/email-already-in-use":
      return "An account with that email already exists.";
    case "auth/too-many-requests":
      return "Too many attempts. Please wait a few minutes and try again.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/weak-password":
      return "Password is too weak. Please use a stronger password.";
    default:
      return "Something went wrong. Please try again.";
  }
}

/**
 * Window events fired by utils/testSession.ts around a custom-token account
 * swap (platform test environment). While a swap is in flight the app drops
 * to its loading screen with no signed-in user, so the previous account's
 * Firestore listeners are torn down before the new token takes effect —
 * otherwise they would fail rule checks under the new identity and log
 * spurious permission errors.
 */
export const ACCOUNT_SWAP_BEGIN_EVENT = "ood:account-swap-begin";
export const ACCOUNT_SWAP_FAILED_EVENT = "ood:account-swap-failed";

export function useAuth() {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingApproval, setPendingApproval] = useState(false);
  const swapPreviousUser = useRef<User | null>(null);

  useEffect(() => {
    const onSwapBegin = () => {
      setCurrentUser((prev) => {
        swapPreviousUser.current = prev;
        return null;
      });
      setLoading(true);
    };
    const onSwapFailed = () => {
      // The token never changed, so put the previous account back.
      setCurrentUser(swapPreviousUser.current);
      swapPreviousUser.current = null;
      setLoading(false);
    };
    window.addEventListener(ACCOUNT_SWAP_BEGIN_EVENT, onSwapBegin);
    window.addEventListener(ACCOUNT_SWAP_FAILED_EVENT, onSwapFailed);
    return () => {
      window.removeEventListener(ACCOUNT_SWAP_BEGIN_EVENT, onSwapBegin);
      window.removeEventListener(ACCOUNT_SWAP_FAILED_EVENT, onSwapFailed);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      swapPreviousUser.current = null;
      try {
        if (firebaseUser) {
          const tokenResult = await firebaseUser.getIdTokenResult();
          if (tokenResult.claims.platformAdmin === true) {
            setCurrentUser({ id: firebaseUser.uid, email: firebaseUser.email || "", platformAdmin: true } as User);
            return;
          }

          // A session minted by the platform test environment carries a
          // `testSession` claim; surface it so the app can show the sandbox
          // banner and the way back to the platform panel.
          const testSession = tokenResult.claims.testSession === true;

          const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
          if (userDoc.exists()) {
            setCurrentUser({
              id: firebaseUser.uid,
              ...(userDoc.data() as object),
              ...(testSession ? { testSession: true } : {}),
            } as User);
          } else {
            setCurrentUser(null);
          }
        } else {
          setCurrentUser(null);
        }
      } catch (err) {
        logger.error("Failed to load user profile:", err);
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    let result;
    try {
      result = await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      recordAuthEvent("login_failed", { email, reason: (err as { code?: string })?.code || "unknown" });
      throw new Error(friendlyAuthError(err));
    }
    recordAuthEvent("login_success", { email });
    try {
      await result.user.getIdToken(true);
    } catch (err) {
      logger.warn("Token refresh after login failed:", (err as Error)?.message);
    }
    const tokenResult = await result.user.getIdTokenResult();
    if (tokenResult.claims.platformAdmin === true) {
      const platformUser = { id: result.user.uid, email: result.user.email || "", platformAdmin: true } as User;
      setCurrentUser(platformUser);
      return platformUser;
    }

    const userDoc = await getDoc(doc(db, "users", result.user.uid));
    if (!userDoc.exists()) {
      throw new Error("Account not found. Please contact your administrator.");
    }

    const userData = userDoc.data() as User;

    if (userData.role !== "entity") {
      if (!userData.agencyId) {
        await signOut(auth);
        throw new Error("Your account is not linked to an agency. Contact your administrator.");
      }

      const agencyDoc = await getDoc(doc(db, "agencies", userData.agencyId));
      if (!agencyDoc.exists() || agencyDoc.data().status !== AGENCY_ACTIVE) {
        await signOut(auth);
        throw new Error("Your agency is no longer active. Contact your administrator.");
      }
    }

    if (userData.status === STATUS_PENDING && userData.role !== "entity") {
      await signOut(auth);
      setPendingApproval(true);
      throw new AuthError("PENDING_APPROVAL", "auth/pending-approval");
    }
    if (userData.status === STATUS_BLOCKED) {
      await signOut(auth);
      throw new Error("Your account has been blocked. Contact your administrator.");
    }

    setPendingApproval(false);
    const profile = { ...userData, id: result.user.uid } as User;
    setCurrentUser(profile);
    return profile;
  }, []);

  const register = useCallback(async (name: string, badge: string, email: string, phone: string, password: string, agencyId: string) => {
    if (!agencyId) {
      throw new Error("Please select your agency.");
    }

    let result;
    try {
      result = await createUserWithEmailAndPassword(auth, email, password);
    } catch (err) {
      throw new Error(friendlyAuthError(err));
    }

    const userProfile = {
      name,
      badge,
      email,
      phone,
      role: "officer" as const,
      status: STATUS_PENDING,
      agencyId,
      notificationPreferences: {
        email: true,
        sms: false,
      },
      createdAt: serverTimestamp(),
    };
    await setDoc(doc(db, "users", result.user.uid), userProfile);

    await signOut(auth);
    setCurrentUser(null);
    setPendingApproval(true);

    return userProfile;
  }, []);

  const registerOrganization = useCallback(async (name: string, orgName: string, email: string, phone: string | undefined, password: string, agencyIds: string[]) => {
    if (!orgName) {
      throw new Error("Organization name is required.");
    }
    if (!Array.isArray(agencyIds) || agencyIds.length === 0) {
      throw new Error("Please select at least one agency to connect with.");
    }

    const functions = getFunctions();
    const selfRegisterEntity = httpsCallable(functions, "selfRegisterEntity");

    try {
      await selfRegisterEntity({
        name,
        orgName,
        email,
        phone: phone || "",
        password,
        agencyIds,
      });
    } catch (err) {
      const e = err as { details?: { message?: string }; message?: string };
      const message = e?.details?.message || e?.message || "Registration failed. Please try again.";
      throw new Error(message.replace(/^FirebaseError: /, ""));
    }

    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const userDoc = await getDoc(doc(db, "users", result.user.uid));
      if (userDoc.exists()) {
        setCurrentUser({ id: result.user.uid, ...(userDoc.data() as object) } as User);
      }
      setPendingApproval(false);
    } catch (err) {
      logger.error("Auto sign-in after registration failed:", err);
      setPendingApproval(true);
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
      recordAuthEvent("password_reset_requested", { email });
    } catch (err) {
      throw new Error(friendlyAuthError(err));
    }
  }, []);

  const updateAuthEmail = useCallback(async (newEmail: string, currentPassword: string) => {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error("Not signed in.");
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await firebaseUpdateEmail(user, newEmail);
    recordAuthEvent("email_changed");
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error("Not signed in.");
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await firebaseUpdatePassword(user, newPassword);
    recordAuthEvent("password_changed");
  }, []);

  const logout = useCallback(async () => {
    await recordAuthEvent("logout");
    await signOut(auth);
    setCurrentUser(null);
  }, []);

  return { currentUser, setCurrentUser, loading, login, register, registerOrganization, logout, resetPassword, updateAuthEmail, changePassword, pendingApproval, setPendingApproval };
}
